import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import PaidIcon from "@mui/icons-material/Paid";
import CategoryIcon from "@mui/icons-material/Category";
import PhotoSizeSelectActualIcon from "@mui/icons-material/PhotoSizeSelectActual";
import { CurrencyRupee } from "@mui/icons-material";
import Apiurl from '../../Helpers/apiUrl';

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "85%", lg: 1500 },
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: "12px",
  border: "1px solid #e0e0e0",
};

const SmartFilterProductManager = ({
  smartFilterProduct,
  setSmartFilterProduct,
  tableData,
  setTableData,
}) => {
  const [productList, setProductList] = useState(smartFilterProduct);
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    if (smartFilterProduct && smartFilterProduct.length > 0) {
      setProductList(smartFilterProduct);
      setOpen(true);
    }
  }, [smartFilterProduct]);

  const handleDelete = (idToDelete) => {
    const updatedList = productList.filter(
      (product) => product.id !== idToDelete
    );
    setProductList(updatedList);
    // setSnackbarMessage("Product deleted successfully!");
    // setSnackbarSeverity("success");
    // setSnackbarOpen(true);

    if (updatedList.length === 0) {
      setTimeout(() => {
        setOpen(false);
        setSmartFilterProduct([]);
      }, 500);
    }
  };

  const handleContinue = () => {
    const productsToAdd = productList
      .filter((product) => product.total > 0)
      .map(product => ({ ...product, productCode: product.sku_code || product.productCode }));

    const productsWithIssues = productList.filter(
      (product) => product.total <= 0
    );

    if (productsToAdd.length === 0 && productsWithIssues.length > 0) {
      setSnackbarMessage(
        "Cannot add products with a total of 0. Please remove them first."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (productsToAdd.length === 0) {
      setSnackbarMessage("No valid products to add!");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    const updatedTableData = tableData.filter(
      (item) => item.sku_code !== "" && item.sku_code !== null
    );
    setTableData([...updatedTableData, ...productsToAdd]);
    setSmartFilterProduct([]);
    setOpen(false);

    setSnackbarMessage(
      `Successfully added ${productsToAdd.length} product(s) to the table!`
    );

    if (productsWithIssues.length > 0) {
      setSnackbarMessage(
        (prev) =>
          prev +
          ` (${productsWithIssues.length} product(s) with zero total were not added.)`
      );
      setSnackbarSeverity("warning");
    } else {
      setSnackbarSeverity("success");
    }
    setSnackbarOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const tableHeaders = [
    { label: "S/N", align: "center", minWidth: 40 },
    { label: "Image", align: "center", minWidth: 80 },
    { label: "Product Description", align: "left", minWidth: 250 },
    { label: "SKU / HSN", align: "left", minWidth: 150 },
    { label: "Collection / Finish", align: "left", minWidth: 180 },
    { label: "Category / Segment", align: "left", minWidth: 180 },
    { label: "MRP / Stock", align: "left", minWidth: 80 },
    { label: "Action", align: "center", minWidth: 80 },
  ];

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="product-management-modal-title"
      >
        <Box sx={style}>
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
            <Typography
              id="product-management-modal-title"
              className="text-blue-800 font-extrabold"
            ></Typography>
            <IconButton onClick={handleClose} className="hover:bg-gray-100">
              <CloseIcon color="action" />
            </IconButton>
          </div>

          <Alert
            severity="info"
            icon={<CheckCircleIcon fontSize="inherit" />}
            className="mb-4 shadow-sm"
          >
            Please **review** and **manage** the imported products below. Items
            with MRP '0' are highlighted in red and **will NOT be added** to
            your selection (System will auto remove those products.).
          </Alert>

          {productList.length > 0 ? (
            <TableContainer
              component={Paper}
              className="max-h-[60vh] overflow-y-auto shadow-lg border border-gray-100 rounded-lg"
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((head, index) => (
                      <TableCell
                        key={index}
                        align={head.align}
                        style={{
                          minWidth: head.minWidth,
                          fontWeight: "bold",
                          fontSize: "0.8rem",
                          backgroundColor: "#f8fafc",
                          color: "#1f2937",
                          textTransform: "uppercase",
                        }}
                        className="border-b border-gray-200"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {head.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {productList.map((product, index) => {
                    const isInvalid =
                      product.total <= 0 || Number(product.stock) === 0;
                    return (
                      <TableRow
                        hover
                        key={product.id || index}
                        className={`transition-colors duration-200 ${
                          isInvalid
                            ? "bg-red-50 hover:bg-red-100"
                            : "hover:bg-blue-50"
                        }`}
                      >
                        <TableCell
                          align="center"
                          className={`font-semibold ${
                            isInvalid ? "text-red-700" : "text-gray-700"
                          }`}
                        >
                          {index + 1}
                        </TableCell>

                        <TableCell align="center">
                          {(() => {
                            const fallback = Apiurl.replace('/api/', '/img/no_image.jpeg');
                            const imageSrc = product.image ? (product.image.startsWith('http') ? product.image : `${Apiurl}${product.image}`) : fallback;
                            return (
                              <img
                                src={imageSrc}
                                alt={product.product_desc || ''}
                                className="w-12 h-12 object-cover rounded-md shadow-sm"
                                onError={(e) => {
                                  if (e.target.src !== fallback) {
                                    e.target.src = fallback;
                                  }
                                }}
                              />
                            );
                          })()}
                        </TableCell>

                        <TableCell align="left">
                          <Typography
                            variant="body2"
                            className={`font-semibold ${
                              isInvalid ? "text-red-800" : "text-gray-800"
                            }`}
                          >
                            {product.product_desc}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Group: {product.Group}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography
                            variant="body2"
                            className="font-medium text-gray-600"
                          >
                            SKU: {product.sku_code}
                          </Typography>
                          <Typography variant="body2" className="text-gray-500">
                            HSN: {product.hsnCode}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography
                            variant="body2"
                            className="font-medium text-gray-600"
                          >
                            Collection: {product.collection}
                          </Typography>
                          <Typography variant="body2" className="text-gray-500">
                            Finish: {product.finish}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography variant="body2" className="text-gray-500">
                            Category: {product.category}
                          </Typography>
                          <Typography variant="body2" className="text-gray-500">
                            Segment: {product.priceSegment}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography
                            variant="body2"
                            className={`${
                              Number(product.mrp) <= 0
                                ? "text-red-500"
                                : "text-gray-500"
                            }`}
                          >
                            MRP: {product.mrp}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={`${
                              Number(product.stock) === 0
                                ? "text-red-500"
                                : "text-gray-500"
                            }`}
                          >
                            Stock: {product.stock}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Remove Product">
                            <IconButton
                              onClick={() => handleDelete(product.id)}
                              size="small"
                              className="hover:bg-red-100"
                            >
                              <DeleteIcon color="error" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="warning" className="mt-4 shadow-sm">
              No products available to review. Please close the modal.
            </Alert>
          )}

          <div className="flex justify-end space-x-4 mt-6">
            <Button
              variant="outlined"
              onClick={handleClose}
              startIcon={<CloseIcon />}
              className="hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinue}
              disabled={productList.length === 0}
              startIcon={<CheckCircleIcon />}
              className="bg-green-600 hover:bg-green-700 transition duration-150 ease-in-out shadow-lg"
            >
              Continue ({productList.filter((p) => p.total > 0).length})
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default SmartFilterProductManager;
